/**
 * DynamicCharge - Charge indicator that follows atom motion
 *
 * Renders a charge symbol (+, -, 2+, etc.) that dynamically tracks its parent atom
 * using Framer Motion MotionValues. Position is calculated as:
 *   chargePos = atomPos + normalize([1, -1, 0]) * chargeOffset
 */

import { memo, useEffect } from 'react';
import { useMotion } from '../../contexts/MotionContext';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { motion, useTransform } from 'motion/react';
import type { ChargeData } from '../../rendering/useSVGGeneration';
import { getFormat } from '../../rendering/utils/formatContext';
import { useSelection } from '../../selection';
import { useFeedbackWithContext } from '../../feedback';

export interface DynamicChargeProps {
  chargeData: ChargeData;
}

export const DynamicCharge = memo(function DynamicCharge({ chargeData }: DynamicChargeProps) {
  const { getAtomMotion } = useMotion();
  const format = useFormatOverride();

  // Generate unique ID for this charge
  const chargeId = `${chargeData.atomId}-charge`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(chargeId);
  const atomMotion = getAtomMotion(chargeData.atomId);

  // Check selection state
  const isSelected = selection?.isSelected(chargeId) ?? false;
  const isHovered = selection?.isHovered(chargeId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Charge offset distance from atom center
  // Use smaller offset (2) for visible small carbons, standard offset (3.4) for other atoms
  const chargeOffset = format.charge.radius * 3.4;

  // Calculate dynamic charge position by adding offset to atom position
  // Motion v12: use function form with .get() for automatic dependency tracking
  // IMPORTANT: All hooks must be called unconditionally to satisfy React's Rules of Hooks
  const chargeX = useTransform(() => {
    const atomX = atomMotion?.x?.get();
    if (typeof atomX !== 'number') return chargeData.pos[0] + chargeData.dir[0] * chargeOffset;
    return atomX + chargeData.dir[0] * chargeOffset;
  });

  const chargeY = useTransform(() => {
    const atomY = atomMotion?.y?.get();
    if (typeof atomY !== 'number') return chargeData.pos[1] + chargeData.dir[1] * chargeOffset;
    return atomY + chargeData.dir[1] * chargeOffset;
  });

  // Pre-compute all derived transforms unconditionally (Rules of Hooks)
  const radius = format.charge.radius;
  const strokeWidth = format.charge.outlineScale;
  const lineLength = radius * format.charge.symbol.length;
  const lineWidth = strokeWidth * format.charge.symbol.width;

  // All useTransform hooks called unconditionally
  const rectHorizX = useTransform(chargeX, (x) => (typeof x === 'number' ? x - lineLength / 2 : 0));
  const rectHorizY = useTransform(chargeY, (y) => (typeof y === 'number' ? y - lineWidth / 2 : 0));
  const rectVertX = useTransform(chargeX, (x) => (typeof x === 'number' ? x - lineWidth / 2 : 0));
  const rectVertY = useTransform(chargeY, (y) => (typeof y === 'number' ? y - lineLength / 2 : 0));

  // Get font size and offset based on charge type
  const getFontSize = (text: string) => {
    switch (text) {
      case '+':
        return format.charge.plus.fontSize;
      case '_':
        return format.charge.minus.fontSize;
      default:
        return format.charge.multiFont;
    }
  };

  const getOffsetY = (text: string) => {
    switch (text) {
      case '+':
        return format.charge.plus.offsetY;
      case '_':
        return format.charge.minus.offsetY;
      default:
        return format.charge.offsetY ?? 0;
    }
  };

  const fontSize = getFontSize(chargeData.chg);
  const offsetY = getOffsetY(chargeData.chg);

  // Text Y position transform (for numeric charges)
  const textY = useTransform(chargeY, (y) => (typeof y === 'number' ? y + offsetY : 0));

  if (!atomMotion) {
    // No motion context - render static at calculated position
    const staticX = chargeData.pos[0] + chargeData.dir[0] * chargeOffset;
    const staticY = chargeData.pos[1] + chargeData.dir[1] * chargeOffset;
    const fontSize = getFormat().charge.multiFont;
    const radius = getFormat().charge.radius ;
    const strokeWidth = getFormat().charge.outlineScale;

    // For + and -, use custom SVG shapes
    if (chargeData.chg === "+") {
      const lineLength = radius * getFormat().charge.symbol.length;
      const lineWidth = strokeWidth * getFormat().charge.symbol.width;
      return (
          <g key={`charge-${chargeData.atomId}`} className="svg-charge"
          style={{pointerEvents: 'none'}}
          >
              <circle
                  cx={staticX}
                  cy={staticY}
                  r={radius}
                  stroke={feedback.currentStyle.stroke ?? "black"}
                  fill={feedback.currentStyle.fill ?? "white"}
                  strokeWidth={strokeWidth}
              />
              {/* Horizontal line */}
              <rect
                  x={staticX - lineLength / 2}
                  y={staticY - lineWidth / 2}
                  width={lineLength}
                  height={lineWidth}
                  fill={"black"}
              />
              {/* Vertical line */}
              <rect
                  x={staticX - lineWidth / 2}
                  y={staticY - lineLength / 2}
                  width={lineWidth}
                  height={lineLength}
                  fill={"black"}
              />
          </g>
      );
    } else if (chargeData.chg === "_") {
      const lineLength = radius * getFormat().charge.symbol.length;
      const lineWidth = strokeWidth * getFormat().charge.symbol.width;
      return (
          <g key={`charge-${chargeData.atomId}`} className="svg-charge" style={{pointerEvents: 'none'}}>
              <circle
                  cx={staticX}
                  cy={staticY}
                  r={radius}
                  stroke={feedback.currentStyle.stroke ?? "black"}
                  fill={feedback.currentStyle.fill ?? "white"}
                  strokeWidth={strokeWidth}
              />
              {/* Horizontal line */}
              <rect
                  x={staticX - lineLength / 2}
                  y={staticY - lineWidth / 2}
                  width={lineLength}
                  height={lineWidth}
                  fill={"black"}
              />
          </g>
      );
    }

    // For other charges (numbers), use text
    return (
      <g key={`charge-${chargeData.atomId}`} className="svg-charge" style={{pointerEvents: 'none'}}>
          <circle
              cx={staticX}
              cy={staticY}
              r={radius}
              stroke={feedback.currentStyle.stroke ?? "black"}
              fill={feedback.currentStyle.fill ?? "white"}
              strokeWidth={strokeWidth}
          />
          <text
              x={staticX}
              y={staticY}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              stroke={"black"}
              fill="black"
              strokeWidth={chargeData.chg === "_" ? ".15px" : "0px"}
          >
              {chargeData.chg}
          </text>
      </g>
    );
  }
  
  // Motion rendering with custom SVG shapes for + and -
  // Uses pre-computed transforms from above (no hooks called here)

  if (chargeData.chg === '+') {
    return (
      <g className="svg-charge" style={{pointerEvents: 'none'}}>
        <motion.circle
          cx={chargeX}
          cy={chargeY}
          r={radius}
          stroke={feedback.currentStyle.stroke ?? "black"}
          fill={feedback.currentStyle.fill ?? "white"}
          strokeWidth={strokeWidth}
        />
        {/* Horizontal line */}
        <motion.rect
          x={rectHorizX}
          y={rectHorizY}
          width={lineLength}
          height={lineWidth}
          fill="black"
        />
        {/* Vertical line */}
        <motion.rect
          x={rectVertX}
          y={rectVertY}
          width={lineWidth}
          height={lineLength}
          fill="black"
        />
      </g>
    );
  } else if (chargeData.chg === '_') {
    return (
      <g className="svg-charge" style={{pointerEvents: 'none'}}>
        <motion.circle
          cx={chargeX}
          cy={chargeY}
          r={radius}
          stroke={feedback.currentStyle.stroke ?? "black"}
          fill={feedback.currentStyle.fill ?? "white"}
          strokeWidth={strokeWidth}
        />
        {/* Horizontal line */}
        <motion.rect
          x={rectHorizX}
          y={rectHorizY}
          width={lineLength}
          height={lineWidth}
          fill="black"
        />
      </g>
    );
  }

  // For other charges (numbers), use text
  return (
    <g className="svg-charge" style={{pointerEvents: 'none'}}>
      <motion.circle
        cx={chargeX}
        cy={chargeY}
        r={radius}
        stroke={feedback.currentStyle.stroke ?? "black"}
        fill={feedback.currentStyle.fill ?? "white"}
        strokeWidth={strokeWidth}
      />
      <motion.text
        x={chargeX}
        y={textY}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        stroke="black"
        fill="black"
        strokeWidth={chargeData.chg === '_' ? '.15px' : '0px'}
      >
        {chargeData.chg}
      </motion.text>
    </g>
  );
});
